/**
 * PreAuctionSetupPanel.js
 * Mandatory setup before starting auction:
 * 1. Confirm which team owners will play for their teams
 * 2. Assign icon players to each team (if enabled)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

// Format BYC amount
const formatBYC = (amount) => {
  if (!amount && amount !== 0) return '0';
  return new Intl.NumberFormat('en-IN').format(amount);
};

export function PreAuctionSetupPanel({ eventId, config, teams, pool, onSetupComplete }) {
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const fetchSetupStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/events/${eventId}/auction/pre-auction/setup-status`);
      setSetupStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch setup status:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchSetupStatus();
  }, [fetchSetupStatus]);

  const handleOwnerSetup = async (teamId, ownerWillPlay, playerPoolId = null, customPrice = null) => {
    setSaving(prev => ({ ...prev, [teamId]: true }));
    try {
      const payload = {
        team_id: teamId,
        owner_will_play: ownerWillPlay,
      };
      // If the selected ID starts with 'owner_', it's an owner without a pool entry
      if (playerPoolId && playerPoolId.startsWith('owner_')) {
        payload.owner_user_id = playerPoolId.replace('owner_', '');
      } else {
        payload.player_pool_id = playerPoolId;
      }
      if (customPrice !== null && customPrice !== undefined) {
        payload.custom_price_byc = parseFloat(customPrice);
      }
      await axiosInstance.post(`/v1/events/${eventId}/auction/pre-auction/confirm-owner-setup`, payload);
      toast.success('Owner setup confirmed');
      fetchSetupStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm setup');
    } finally {
      setSaving(prev => ({ ...prev, [teamId]: false }));
    }
  };

  const handleUndoOwnerSetup = async (teamId) => {
    setSaving(prev => ({ ...prev, [teamId]: true }));
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/pre-auction/undo-owner-setup`, {
        team_id: teamId
      });
      toast.success('Owner setup reverted');
      fetchSetupStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to undo setup');
    } finally {
      setSaving(prev => ({ ...prev, [teamId]: false }));
    }
  };

  const handleIconAssign = async (teamId, playerPoolId) => {
    setSaving(prev => ({ ...prev, [`icon_${teamId}`]: true }));
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/pool/${playerPoolId}/assign-icon-team`, {
        team_id: teamId
      });
      toast.success('Icon player assigned');
      fetchSetupStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign icon');
    } finally {
      setSaving(prev => ({ ...prev, [`icon_${teamId}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#E54562] border-t-transparent"></div>
      </div>
    );
  }

  if (!setupStatus) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        Failed to load pre-auction setup status
      </div>
    );
  }

  const { teams: teamStatuses, enable_owner_participation, enable_icon_players, 
          owner_flat_price_byc, icon_player_fixed_price_byc, ready_to_start } = setupStatus;

  // Get available players for owner selection (players who are team owners)
  const getOwnerPlayersForTeam = (teamId) => {
    const teamData = teams?.find(t => t.id === teamId);
    if (!teamData?.owners) return [];
    
    // Match owners to pool entries by user_id
    const ownerPoolMatches = pool?.filter(p => {
      return p.status === 'AVAILABLE' && 
             teamData.owners.some(o => o.user_id === p.user_id);
    }) || [];
    
    // Also include owners who DON'T have a pool entry (registered as team_owner only)
    const matchedUserIds = new Set(ownerPoolMatches.map(p => p.user_id));
    const ownersWithoutPool = teamData.owners
      .filter(o => !matchedUserIds.has(o.user_id))
      .map(o => ({
        id: `owner_${o.user_id}`,  // synthetic ID
        user_id: o.user_id,
        player_name: o.user_name,
        registration_id: o.registration_id,
        status: 'AVAILABLE',
        is_owner_only: true,  // flag to distinguish from pool entries
      }));
    
    return [...ownerPoolMatches, ...ownersWithoutPool];
  };

  // Get available icon players (any available player who is marked as icon)
  const getAvailableIconPlayers = () => {
    return pool?.filter(p => p.is_icon && !p.icon_assigned_team_id && p.status !== 'EXCLUDED') || [];
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="pre-auction-setup">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2B3A67] to-[#3d4f87] p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <i className="ph-bold ph-gear-six"></i>
              Pre-Auction Setup
            </h3>
            <p className="text-sm text-white/70 mt-1">
              Complete these steps before starting the auction
            </p>
          </div>
          {ready_to_start ? (
            <span className="bg-green-500 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2">
              <i className="ph-bold ph-check-circle"></i>
              Ready to Start
            </span>
          ) : (
            <span className="bg-yellow-500 text-yellow-900 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2">
              <i className="ph-bold ph-warning-circle"></i>
              Setup Incomplete
            </span>
          )}
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* Owner Player Setup */}
        {enable_owner_participation && (
          <div>
            <h4 className="font-bold mb-3 flex items-center gap-2">
              <i className="ph-bold ph-user-circle text-purple-500"></i>
              Step 1: Owner-Player Setup
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full ml-2">
                Fee: ₿ {formatBYC(owner_flat_price_byc)}
              </span>
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              For each team, confirm whether the owner will play for their team. 
              If yes, the owner fee will be deducted from the team's wallet.
            </p>
            
            <div className="space-y-3">
              {teamStatuses.map(team => (
                <TeamOwnerSetupCard 
                  key={team.team_id}
                  team={team}
                  ownerPrice={owner_flat_price_byc}
                  pool={pool}
                  saving={saving[team.team_id]}
                  onConfirm={(willPlay, playerId, customPrice) => handleOwnerSetup(team.team_id, willPlay, playerId, customPrice)}
                  getOwnerPlayers={getOwnerPlayersForTeam}
                  onUndo={handleUndoOwnerSetup}
                />
              ))}
            </div>
          </div>
        )}

        {/* Icon Player Setup */}
        {enable_icon_players && (
          <div>
            <h4 className="font-bold mb-3 flex items-center gap-2">
              <i className="ph-bold ph-star text-yellow-500"></i>
              {enable_owner_participation ? 'Step 2' : 'Step 1'}: Icon Player Assignment
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full ml-2">
                Fee: ₿ {formatBYC(icon_player_fixed_price_byc)}
              </span>
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              Assign an icon player to each team. The icon fee will be deducted from the team's wallet.
            </p>
            
            <div className="space-y-3">
              {teamStatuses.map(team => (
                <TeamIconSetupCard 
                  key={team.team_id}
                  team={team}
                  iconPrice={icon_player_fixed_price_byc}
                  availableIcons={getAvailableIconPlayers()}
                  saving={saving[`icon_${team.team_id}`]}
                  onAssign={(playerId) => handleIconAssign(team.team_id, playerId)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className={`rounded-xl p-4 ${ready_to_start ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-bold ${ready_to_start ? 'text-green-700' : 'text-yellow-700'}`}>
                {ready_to_start ? 'All pre-auction setup complete!' : 'Please complete all setup steps'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {!ready_to_start && (
                  <>
                    {!setupStatus.all_owner_setup_complete && enable_owner_participation && 
                      'Owner-player setup pending for some teams. '}
                  </>
                )}
              </p>
            </div>
            {ready_to_start && (
              <button
                onClick={onSetupComplete}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2 transition-colors"
              >
                <i className="ph-bold ph-play"></i>
                Proceed to Start
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Team Owner Setup Card
function TeamOwnerSetupCard({ team, ownerPrice, pool, saving, onConfirm, getOwnerPlayers, onUndo }) {
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [showOptions, setShowOptions] = useState(!team.owner_player_setup_complete);

  // Get matching owner pool entries + all available players as fallback
  const ownerPoolEntries = getOwnerPlayers(team.team_id);
  const otherAvailable = pool?.filter(p => 
    p.status === 'AVAILABLE' && !p.is_owner_player && 
    !ownerPoolEntries.some(o => o.id === p.id)
  ) || [];

  if (team.owner_player_setup_complete) {
    return (
      <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold">{team.team_name[0]}</span>
            )}
          </div>
          <div>
            <p className="font-bold">{team.team_name}</p>
            <p className="text-sm text-gray-500">
              {team.owner_will_play ? (
                <span className="text-green-600">
                  <i className="ph-bold ph-check-circle mr-1"></i>
                  Owner playing (₿ {formatBYC(ownerPrice)} deducted)
                </span>
              ) : (
                <span className="text-gray-400">
                  <i className="ph-bold ph-x-circle mr-1"></i>
                  Owner not playing
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => onUndo(team.team_id)}
          disabled={saving}
          className="px-3 py-1.5 border-2 border-amber-500 text-amber-600 font-bold text-xs rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
          data-testid={`undo-owner-setup-${team.team_id}`}
        >
          <i className="ph-bold ph-arrow-counter-clockwise mr-1"></i>Undo
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold">{team.team_name[0]}</span>
            )}
          </div>
          <div>
            <p className="font-bold">{team.team_name}</p>
            <p className="text-xs text-gray-500">Balance: ₿ {formatBYC(team.wallet_balance_byc)}</p>
          </div>
        </div>
        <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full font-bold">
          Pending
        </span>
      </div>

      <div className="space-y-3">
        {/* Custom price input */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 font-medium whitespace-nowrap">Deduction (₿):</label>
          <input
            type="number"
            value={customPrice}
            onChange={(e) => setCustomPrice(e.target.value)}
            placeholder={ownerPrice?.toString() || '0'}
            min={0}
            className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            data-testid={`owner-price-input-${team.team_id}`}
          />
          <span className="text-[10px] text-gray-400">Default: ₿ {formatBYC(ownerPrice || 0)}</span>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => onConfirm(false, null, customPrice || null)}
            disabled={saving}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <i className="ph-bold ph-x mr-1"></i>
            Owner Won't Play
          </button>
          
          <div className="flex-1 flex gap-2">
            <select
              value={selectedPlayer}
              onChange={(e) => setSelectedPlayer(e.target.value)}
              className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select owner...</option>
              {ownerPoolEntries.length > 0 && (
                <optgroup label="Team Owners">
                  {ownerPoolEntries.map(p => (
                    <option key={p.id} value={p.id}>{p.player_name}</option>
                  ))}
                </optgroup>
              )}
              {otherAvailable.length > 0 && (
                <optgroup label="Other Players">
                  {otherAvailable.map(p => (
                    <option key={p.id} value={p.id}>{p.player_name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              onClick={() => selectedPlayer && onConfirm(true, selectedPlayer, customPrice || null)}
              disabled={saving || !selectedPlayer}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
            {saving ? (
              <i className="ph-bold ph-spinner animate-spin"></i>
            ) : (
              <>
                <i className="ph-bold ph-check mr-1"></i>
                Will Play
              </>
            )}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

// Team Icon Setup Card
function TeamIconSetupCard({ team, iconPrice, availableIcons, saving, onAssign }) {
  const [selectedIcon, setSelectedIcon] = useState('');

  if (team.icon_assigned) {
    return (
      <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border border-yellow-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold">{team.team_name[0]}</span>
            )}
          </div>
          <div>
            <p className="font-bold">{team.team_name}</p>
            <div className="flex items-center gap-2">
              <i className="ph-bold ph-star text-yellow-500"></i>
              <span className="text-sm font-medium">{team.icon_assigned.player_name}</span>
              <span className="text-xs text-gray-400">
                (₿ {formatBYC(team.icon_assigned.sold_price_byc || iconPrice)})
              </span>
            </div>
          </div>
        </div>
        <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-bold">
          <i className="ph-bold ph-star mr-1"></i>Assigned
        </span>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2B3A67] flex items-center justify-center overflow-hidden">
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt={team.team_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold">{team.team_name[0]}</span>
            )}
          </div>
          <div>
            <p className="font-bold">{team.team_name}</p>
            <p className="text-xs text-gray-500">Balance: ₿ {formatBYC(team.wallet_balance_byc)}</p>
          </div>
        </div>
        <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-bold">
          No Icon
        </span>
      </div>

      <div className="flex gap-2">
        <select
          value={selectedIcon}
          onChange={(e) => setSelectedIcon(e.target.value)}
          className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Select icon player...</option>
          {availableIcons.map(p => (
            <option key={p.id} value={p.id}>
              {p.player_name} ({p.player_category || 'N/A'})
            </option>
          ))}
        </select>
        <button
          onClick={() => selectedIcon && onAssign(selectedIcon)}
          disabled={saving || !selectedIcon}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
        >
          {saving ? (
            <i className="ph-bold ph-spinner animate-spin"></i>
          ) : (
            <>
              <i className="ph-bold ph-star mr-1"></i>
              Assign Icon
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default PreAuctionSetupPanel;
